import * as qs from "query-string";

import { allStates } from "../../../constants/states/all";
import {
  Custom,
  ProductBuilderMachineContext,
  ProductBuilderMachineState,
  TWindow,
  AddonProduct,
} from "../../../types";

type GetCustomStateFromParams = (params: string) => Custom;
type Entry = [ProductBuilderMachineState, string | string[] | null | undefined];
type ParseEntry = (entry: Entry) => [string, any];
type UpdateInitialContext = (custom: Custom) => any;

const parseEntry: ParseEntry = ([key, value]) => {
  switch (key) {
    // [key, 'no' || 'variantId']
    case "measureInsurance":
    case "remoteControl":
    case "fascia":
    case "extensionPole":
    case "wifiHub": {
      if (value === "no") return [key, value];
      if (typeof value === "string") return [key, parseInt(value)];
      return [key, value];
    }
    // [key, ['variantId', '' || 'left' || 'right']]
    case "powerSupply": {
      if (Array.isArray(value)) {
        return [key, [parseInt(value[0]), value[1]]];
      }
      return [key, value];
    }
    // Price fields - convert strings to numbers (prices are in cents)
    case "wand_colour_price":
    case "track_colour_price":
    case "lining_colour_price":
    case "measureInsurancePrice":
    case "fasciaPrice":
    case "smartReleasePrice":
    case "lightGuardPrice":
    case "venetianValancePrice":
    case "trackPrice":
    case "extensionPolePrice":
    case "stainlessSteelChainPrice":
    case "honeycombVerticalCentreOpeningSurchargePrice":
    case "dynamicPrice": {
      if (typeof value === "string") {
        const numValue = parseFloat(value);
        return [key, isNaN(numValue) ? 0 : numValue];
      }
      return [key, typeof value === "number" ? value : 0];
    }
    // Variant ID fields - convert strings to numbers
    case "wand_colour_variant_id":
    case "track_colour_variant_id":
    case "lining_colour_variant_id": {
      if (typeof value === "string") {
        const numValue = parseInt(value);
        return [key, isNaN(numValue) ? undefined : numValue];
      }
      return [key, typeof value === "number" ? value : undefined];
    }
    // anything else, do nothing
    default: {
      return [key, value];
    }
  }
};

const getCustomStateFromParams: GetCustomStateFromParams = (params) => {
  const parsedParams = qs.parse(params);
  
  // Parse all entries, filtering out invalid ones instead of rejecting everything
  const parsedEntries = Object.entries(parsedParams)
    .filter(([key, value]) => {
      const keyIsValid = allStates.includes(key as ProductBuilderMachineState);
      const valueIsValid =
        typeof value === "string" || (Array.isArray(value) && value.length === 2);
      return keyIsValid && valueIsValid;
    })
    .map(([key, value]) => {
      return parseEntry([key as ProductBuilderMachineState, value]);
    });
  
  const parsed = Object.fromEntries(parsedEntries);

  // Always return parsed values (even if empty), but clear measureInsurance
  return { ...parsed, measureInsurance: undefined };
};

const isDev = process.env.NODE_ENV === "development";

const _window = window as TWindow;

const path = window.location.pathname;
let productsPath = "";
let stubFreeSamples;

// Support for multipal product types.
switch (path) {
  case "/venetianBlinds-timber":
    productsPath = "stub/products-venetianBlinds-timber.json";
    break;

  case "/venetianBlinds-pvc":
    productsPath = "stub/products-venetianBlinds-pvc.json";
    break;

  case "/venetianBlinds-al":
    productsPath = "stub/products-venetianBlinds-aluminium.json";
    break;

  case "/roman":
    productsPath = "stub/products-roman.json";
    break;

  case "/roman-chain":
    productsPath = "stub/products-roman-chain.json";
    break;

  case "/roman-chainless":
    productsPath = "stub/products-roman-chainless.json";
    break;

  case "/curtain":
    productsPath = "stub/products-curtain.json";
    stubFreeSamples = "stub/products-curtain.json";
    break;

  case "/curtains":
    productsPath = "stub/products-curtain.json";
    stubFreeSamples = "stub/products-curtain.json";
    break;

  case "/curtain-lx":
    productsPath = "stub/products-curtain-lx.json";
    break;

  case "/hc":
    productsPath = "stub/products-hc.json";
    stubFreeSamples = "stub/products-hc.json";
    break;

  case "/hc-daynight":
    productsPath = "stub/products-hc-daynight.json";
    break;

  case "/hc-skylight":
    productsPath = "stub/products-honeycomb-skylight.json";
    break;

  case "/roller":
    productsPath = "stub/product-roller.json";
    stubFreeSamples = "stub/products-roller-single.json";
    break;

  case "/roller-premium":
    productsPath = "stub/products-roller-premium.json";
    stubFreeSamples = "stub/products-roller-premium.json";
    break;

  case "/roller-day-night":
    productsPath = "stub/products-roller-day-night.json";
    stubFreeSamples = "stub/products-roller-day-night.json";
    break;

  case "/hc-smartfit-day-night":
    productsPath = "stub/products-honeycomb-smartfit-day-night.json";
    stubFreeSamples = "stub/products-honeycomb-smartfit-day-night.json";
    break;

  default:
    productsPath = "stub/products-shutters.json";
}

const products = isDev ? require(`../${productsPath}`) : _window.products;


const measureInsurance = isDev
  ? require("../stub/measureInsurance.json")
  : _window.measureInsurance;

const powerSupply = isDev
  ? require("../stub/powerSupply.json")
  : _window.powerSupply;

const remoteControl = isDev
  ? require("../stub/remoteControl.json")
  : _window.remoteControl;

const extensionPole = isDev
  ? require("../stub/extensionPole.json")
  : _window.extensionPole;

const stainlessSteelChain = isDev ? "" : _window.stainlessSteelChain;

const wifiHub = isDev ? require("../stub/wifiHub.json") : _window.wifiHub;
const fascia = isDev ? "" : _window.fascia;
const store = isDev ? require("../stub/store.json") : _window.store;
const smartRelease = isDev
  ? require("../stub/smartRelease.json")
  : _window.smartRelease;
const customer = _window.customer;

const venetianValance = isDev ? "" : _window.venetianValance;
const ceilingMountPleatedCurtainRail = isDev
  ? ""
  : _window.ceilingMountPleatedCurtainRail;
const faceMountPleatedCurtainRail = isDev
  ? ""
  : _window.faceMountPleatedCurtainRail;
const ceilingMountWaveFoldCurtainRail = isDev
  ? ""
  : _window.ceilingMountWaveFoldCurtainRail;
const faceMountWaveFoldCurtainRail = isDev
  ? ""
  : _window.faceMountWaveFoldCurtainRail;

const ceilingMountPleatedCurtainDoubleRail = isDev
  ? ""
  : _window.ceilingMountPleatedCurtainDoubleRail;
const faceMountPleatedCurtainDoubleRail = isDev
  ? ""
  : _window.faceMountPleatedCurtainDoubleRail;
const ceilingMountWaveFoldCurtainDoubleRail = isDev
  ? ""
  : _window.ceilingMountWaveFoldCurtainDoubleRail;
const faceMountWaveFoldCurtainDoubleRail = isDev
  ? ""
  : _window.faceMountWaveFoldCurtainDoubleRail;

const lightGuards = isDev ? "" : _window.lightGuards;

const honeycombVerticalCentreOpeningSurcharge = isDev
  ? ""
  : _window.honeycombVerticalCentreOpeningSurcharge;

const colourSamples = isDev
  ? require(`../${stubFreeSamples}`)
  : _window.colourSamples;

// Wand color options loaded from window like other addon products
const wandblack = isDev ? _window.wandblack || "" : _window.wandblack;
const wandwhite = isDev ? _window.wandwhite || "" : _window.wandwhite;

// Track color options loaded from window like other addon products
const trackblack = isDev ? _window.trackblack || "" : _window.trackblack;
const trackwhite = isDev ? _window.trackwhite || "" : _window.trackwhite;

// Lining color options loaded from window like other addon products
const curtainblock = isDev ? _window.curtainblock || "" : _window.curtainblock;
const curtainsemi = isDev ? _window.curtainsemi || "" : _window.curtainsemi;

// Temporary fallback for testing - remove when Shopify products are set up
const getFallbackLiningProducts = () => {
  if (isDev) return [];

  // If window objects are not loaded, provide fallback data
  if (!_window.curtainblock || !_window.curtainsemi) {
    return {
      curtainblock: [
        {
          id: "fallback-blockout",
          title: "Blockout Lining",
          variants: [
            { id: 400001, title: "400mm", price: 5000 },
            { id: 500001, title: "500mm", price: 6000 },
            { id: 600001, title: "600mm", price: 7000 },
            { id: 700001, title: "700mm", price: 8000 },
            { id: 800001, title: "800mm", price: 9000 },
          ],
        },
      ],
      curtainsemi: [
        {
          id: "fallback-semi",
          title: "Semi-Transparent Lining",
          variants: [
            { id: 400002, title: "400mm", price: 4500 },
            { id: 500002, title: "500mm", price: 5500 },
            { id: 600002, title: "600mm", price: 6500 },
            { id: 700002, title: "700mm", price: 7500 },
            { id: 800002, title: "800mm", price: 8500 },
          ],
        },
      ],
    };
  }
  return null;
};

const fallbackLining = getFallbackLiningProducts();
const finalCurtainblock =
  curtainblock ||
  (fallbackLining && "curtainblock" in fallbackLining
    ? fallbackLining.curtainblock
    : []);
const finalCurtainsemi =
  curtainsemi ||
  (fallbackLining && "curtainsemi" in fallbackLining
    ? fallbackLining.curtainsemi
    : []);

const updateInitialContext: UpdateInitialContext = (custom) => {
  const productWithMeasurements = localStorage.getItem(
    "productWithMeasurements"
  );
  const productUrl = localStorage.getItem("productUrl");
  var currentPath = window.location.pathname;

  if (currentPath.includes("shutter")) {
    custom.shipping = "sea";
  } else {
    custom.shipping = "air";
  }

  if (
    customer != null &&
    productUrl &&
    productUrl.indexOf(currentPath) > -1 &&
    productWithMeasurements
  ) {
    localStorage.removeItem("productWithMeasurements");
    localStorage.removeItem("productUrl");
    return {
      productWithMeasurements: JSON.parse(productWithMeasurements),
    };
  } else {
    // When building another product, only clear measurements
    // Keep other selections (lining, fitting, configuration) but clear their prices
    // Addon prices will be recalculated when user reaches those steps
    const clearedCustom = { ...custom };
    let shouldClearProductWithMeasurements = false;
    
    // Only clear measurements - user must re-enter to get fresh price calculation
    if (custom.measurements) {
      clearedCustom.measurements = undefined;
      // Clear dynamicPrice to force recalculation with new measurements
      clearedCustom.dynamicPrice = 0;
      // Mark that we should clear productWithMeasurements to force recalculation
      shouldClearProductWithMeasurements = true;
    }
    
    // Clear addon prices - they will be recalculated when user reaches those steps
    // This ensures price only shows API calculation until user selects addons
    // But preserve all other selections (colour, pleatType, transparency, etc.)
    const result: any = {
      custom: {
        ...clearedCustom, // This includes all preserved values from URL (colour, pleatType, etc.)
        // Only override price fields, keep all other selections
        measureInsurancePrice: custom.measureInsurancePrice ?? 0,
        fasciaPrice: custom.fasciaPrice ?? 0,
        smartReleasePrice: custom.smartReleasePrice ?? 0,
        lightGuardPrice: custom.lightGuardPrice ?? 0,
        venetianValancePrice: custom.venetianValancePrice ?? 0,
        trackPrice: custom.trackPrice ?? 0,
        extensionPolePrice: custom.extensionPolePrice ?? 0,
        stainlessSteelChainPrice: custom.stainlessSteelChainPrice ?? 0,
        honeycombVerticalCentreOpeningSurchargePrice:
          custom.honeycombVerticalCentreOpeningSurchargePrice ?? 0,
        dynamicPrice: 0, // Always start at 0, will be set by API
        // Clear curtain-specific addon prices - will be recalculated when user reaches those steps
        wand_colour_price: 0,
        track_colour_price: 0,
        lining_colour_price: 0,
      },
    };
    
    // Clear productWithMeasurements if measurements were cleared
    if (shouldClearProductWithMeasurements) {
      result.productWithMeasurements = null;
    }
    
    return result;
  }
};

const custom = getCustomStateFromParams(window.location.search);
// Ensure wand option defaults to 'No' at initialization so navigation enables immediately
if (
  !("wand_option" in custom) ||
  custom.wand_option === undefined ||
  custom.wand_option === null ||
  custom.wand_option === ""
) {
  (custom as any).wand_option = "No";
  // Set wand colour to 'white' by default
  (custom as any).wand_colour = "white";
  (custom as any).wand_colour_variant_id = undefined;
  (custom as any).wand_colour_price = 0;
}

// Default track colour to 'White' so navigation enables on trackColour step
if (
  !("trackColour" in custom) ||
  (custom as any).trackColour === undefined ||
  (custom as any).trackColour === null ||
  (custom as any).trackColour === ""
) {
  (custom as any).trackColour = "White";
}

// Default trackRequire for curtain types
if (
  !("trackRequire" in custom) ||
  (custom as any).trackRequire === undefined ||
  (custom as any).trackRequire === null ||
  (custom as any).trackRequire === ""
) {
  (custom as any).trackRequire = "No"; // Default to No for curtain types
  (custom as any).trackColour = "No"; // Set trackColour to "No" when trackRequire is "No"
}
const currentPath = window.location.pathname;

if (currentPath.includes("shutter")) {
  custom.shipping = "sea";
} else {
  custom.shipping = "air";
}
export const initialContext: ProductBuilderMachineContext = {
  products,
  colourSamples,
  measureInsurance,
  powerSupply,
  remoteControl,
  stainlessSteelChain,
  fascia,
  extensionPole,
  smartRelease,
  wifiHub,
  venetianValance,
  lightGuards,
  ceilingMountPleatedCurtainRail,
  faceMountPleatedCurtainRail,
  ceilingMountWaveFoldCurtainRail,
  faceMountWaveFoldCurtainRail,
  ceilingMountPleatedCurtainDoubleRail,
  faceMountPleatedCurtainDoubleRail,
  ceilingMountWaveFoldCurtainDoubleRail,
  faceMountWaveFoldCurtainDoubleRail,
  honeycombVerticalCentreOpeningSurcharge,
  wandblack,
  wandwhite,
  trackblack,
  trackwhite,
  curtainblock: finalCurtainblock,
  curtainsemi: finalCurtainsemi,
  productWithMeasurements: null,
  quantity: 1,
  cart: null,
  store,
  custom,
  customer,
  supplier: (() => {
    const extractedSupplier = products && products[0] && products[0].metafields && products[0].metafields.supplier 
      ? products[0].metafields.supplier 
      : '';
    return extractedSupplier;
  })(),
  ...updateInitialContext(custom),
};
